import { Link } from "react-router-dom";
import { Award, Target, Users, ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useSeo } from "@/lib/seo";

const VALUES = [
  {
    icon: Target,
    title: "Berorientasi Solusi",
    desc: "Kami fokus pada hasil nyata yang berdampak pada kinerja organisasi klien.",
  },
  {
    icon: ShieldCheck,
    title: "Integritas & Transparansi",
    desc: "Penawaran, pelaksanaan, dan pelaporan dilakukan dengan standar etika tinggi.",
  },
  {
    icon: Users,
    title: "Kolaboratif",
    desc: "Kami menjadi perpanjangan tim TI Anda — bukan vendor yang lepas tangan.",
  },
  {
    icon: Award,
    title: "Berkualitas",
    desc: "Engineer bersertifikasi, perangkat resmi, dan proses QA berlapis.",
  },
];

const AboutPage = () => {
  useSeo({
    title: "Tentang Kami",
    description:
      "CV. Komunika Teknik — perusahaan teknologi informasi dengan pengalaman 10+ tahun dalam pengembangan sistem, pengadaan TI, dan konsultasi untuk B2B Indonesia.",
    path: "/tentang",
  });

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]" data-testid="about-page">
      <Header />

      <section className="bg-[#0F172A] text-white pt-24 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 kt-grid-bg opacity-10" />
        <div className="kt-container relative">
          <div className="kt-eyebrow text-[#FCA5A5]">Tentang Perusahaan</div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] max-w-4xl">
            CV. Komunika Teknik —
            <br />
            <span className="text-[#DC2626]">Sepuluh tahun</span> mewujudkan
            sistem teknologi yang bekerja.
          </h1>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="kt-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="kt-eyebrow">Profil Perusahaan</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-4 leading-tight">
              Mitra strategis transformasi digital di Indonesia.
            </h2>
            <div className="kt-divider-red mt-6" />
            <div className="mt-6 space-y-4 text-[#52525B] leading-relaxed">
              <p>
                CV. Komunika Teknik adalah perusahaan teknologi informasi yang
                berfokus pada tiga pilar utama: <strong>pengembangan sistem
                informasi</strong>, <strong>pengadaan perangkat TI</strong>,
                dan <strong>konsultasi teknologi</strong>. Kami telah melayani
                instansi pemerintah, BUMN, dan korporasi swasta sejak satu
                dekade lebih.
              </p>
              <p>
                Dengan tim engineer bersertifikasi dan jaringan mitra principal
                resmi, kami menghadirkan solusi end-to-end mulai dari
                perancangan, pengadaan, implementasi, hingga pemeliharaan
                jangka panjang.
              </p>
              <p>
                Komitmen kami sederhana: <strong>menyampaikan solusi yang
                bekerja</strong> — tepat waktu, sesuai anggaran, dan didukung
                penuh setelah serah terima.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src="https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&w=1400"
              alt="Tim Komunika Teknik"
              className="w-full h-[460px] object-cover border border-[#E4E4E7]"
            />
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#FAFAFA] kt-grid-bg">
        <div className="kt-container">
          <div className="kt-eyebrow">Nilai-Nilai Kami</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-4 leading-tight max-w-2xl">
            Empat prinsip yang memandu setiap pekerjaan kami.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v) => (
              <div
                key={v.title}
                className="kt-card p-7"
                data-testid={`about-value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-12 h-12 bg-[#18181B] text-white grid place-items-center">
                  <v.icon size={22} />
                </div>
                <h3 className="font-display font-bold text-lg mt-6">{v.title}</h3>
                <p className="mt-3 text-sm text-[#52525B] leading-relaxed">
                  {v.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="kt-container border border-[#E4E4E7] bg-[#FAFAFA] p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">
              Tertarik bermitra dengan kami?
            </h2>
            <p className="mt-4 text-[#52525B]">
              Mari diskusikan ruang lingkup proyek Anda secara terperinci.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/kontak"
              className="kt-btn-primary"
              data-testid="about-cta-contact"
            >
              Hubungi Tim
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
